// DisplayLogicSection.js
import React, { useState, useEffect } from 'react';
import { HelpCircle, Plus, X } from 'lucide-react';

const DisplayLogicSection = ({ 
  formData, 
  validationErrors, 
  airtableFields,
  toggleSection,
  addDisplayField,
  removeDisplayField
}) => {
  // New display field state
  const [newDisplayField, setNewDisplayField] = useState({
    record: 'validate',
    field: {
      id: '',
      name: ''
    },
    label: '',
    hide_if_empty: false
  });

  // Display Logic handlers
  const handleDisplayRecordChange = (e) => {
    const record = e.target.value;
    
    setNewDisplayField(prev => ({
      ...prev,
      record,
      field: {
        id: '',
        name: ''
      }
    }));
  };

  const handleDisplayFieldSelect = (e) => {
    const fieldId = e.target.value;
    const fieldName = e.target.options[e.target.selectedIndex].text;
    
    setNewDisplayField(prev => ({
      ...prev,
      field: {
        id: fieldId,
        name: fieldName
      }
    }));
  };

  const handleDisplayLabelChange = (e) => {
    const label = e.target.value;
    
    setNewDisplayField(prev => ({
      ...prev,
      label
    }));
  };

  const handleDisplayHideChange = (e) => {
    const hideIfEmpty = e.target.checked;
    
    setNewDisplayField(prev => ({
      ...prev,
      hide_if_empty: hideIfEmpty
    }));
  };

  const handleAddDisplayField = () => {
    // Don't add if field is empty
    if (!newDisplayField.field.id) return;
    
    addDisplayField({ ...newDisplayField });
    
    // Reset new display field
    setNewDisplayField({
      record: 'validate',
      field: {
        id: '',
        name: ''
      },
      label: '',
      hide_if_empty: false
    });
  };

  // Determine which fields to show based on the selected record type
  const getAvailableFields = () => {
    if (newDisplayField.record === 'validate') {
      return airtableFields.validate || [];
    } else if (newDisplayField.record === 'create') {
      return airtableFields.create || [];
    }
    return [];
  };

  // Reset record selection to 'validate' if offline is enabled and 'create' is selected
  useEffect(() => {
    if (formData.hasOfflineLogic && newDisplayField.record === 'create') {
      setNewDisplayField(prev => ({
        ...prev,
        record: 'validate',
        field: {
          id: '',
          name: ''
        }
      }));
    }
  }, [formData.hasOfflineLogic, newDisplayField.record]);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium flex items-center">
          <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${formData.hasDisplayLogic ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
            <HelpCircle className="h-4 w-4" />
          </span>
          Display Logic
        </h3>
        <div className="relative inline-block w-10 mr-2 align-middle select-none">
          <input
            type="checkbox"
            id="toggle-display"
            className="sr-only"
            checked={formData.hasDisplayLogic}
            onChange={toggleSection}
          />
          <label 
            htmlFor="toggle-display"
            className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formData.hasDisplayLogic ? 'bg-indigo-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute block h-4 w-4 rounded-full bg-white top-1 transition-transform duration-200 ease-in-out ${formData.hasDisplayLogic ? 'right-1 transform translate-x-0' : 'left-1 transform translate-x-0'}`}></span>
          </label>
        </div>
      </div>
      
      {formData.hasDisplayLogic && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Configure which fields to display to the user after a successful scan.
          </p>

          {formData.hasOfflineLogic && (
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-orange-700">
                    Offline mode is enabled. Only fields from validated records can be displayed since created records are not available until synced.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {(validationErrors.displayLogic || validationErrors.displayRecords) && (
            <div className="mb-2 text-sm text-red-500">
              {validationErrors.displayLogic || validationErrors.displayRecords}
            </div>
          )}
          
          {validationErrors.displayFields && (
            <p className="mb-2 text-sm text-red-500">{validationErrors.displayFields}</p>
          )}
          
          {/* Existing display fields */}
          <div className="space-y-2 mb-4">
            {formData.display_logic.map((display, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-2 rounded">
                <div className="flex-grow">
                  <span className="text-sm">
                    <span className={`px-2 py-0.5 rounded ${display.record === 'validate' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                      {display.record}
                    </span>
                    <strong className="ml-2">{display.field.name}</strong>
                    {display.label && <span className="mx-1">as</span>}
                    {display.label && <em>"{display.label}"</em>}
                    {display.hide_if_empty && (
                      <span className="ml-2 text-gray-500">(Hidden if empty)</span>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeDisplayField(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new display field */}
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-2">
              <select
                value={newDisplayField.record}
                onChange={handleDisplayRecordChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="validate" disabled={!formData.hasValidateLogic}>Validate</option>
                <option value="create" disabled={!formData.hasCreateLogic || formData.hasOfflineLogic}>
                  Create {formData.hasOfflineLogic ? '(Not available offline)' : ''}
                </option>
              </select>
            </div>
            <div className="col-span-4">
              <select
                value={newDisplayField.field.id}
                onChange={handleDisplayFieldSelect}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                  ((newDisplayField.record === 'validate' && !formData.validate_logic.search_table.id) ||
                  (newDisplayField.record === 'create' && !formData.create_logic.create_table.id))
                  ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                disabled={
                  (newDisplayField.record === 'validate' && !formData.validate_logic.search_table.id) ||
                  (newDisplayField.record === 'create' && !formData.create_logic.create_table.id)
                }
              >
                <option value="">Select field</option>
                {getAvailableFields().map(field => (
                  <option key={field.id} value={field.id}>{field.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-3">
              <input
                type="text"
                placeholder="Display label (optional)"
                value={newDisplayField.label}
                onChange={handleDisplayLabelChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="col-span-2">
              <div className="flex items-center h-full">
                <input
                  type="checkbox"
                  id="hide-if-empty"
                  checked={newDisplayField.hide_if_empty}
                  onChange={handleDisplayHideChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hide-if-empty" className="ml-2 block text-sm text-gray-900">
                  Hide if empty
                </label>
              </div>
            </div>
            <div className="col-span-1">
              <button
                type="button"
                onClick={handleAddDisplayField}
                disabled={!newDisplayField.field.id}
                className="h-full w-full flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Configure which fields will be shown to the scanner user.
          </p>
        </div>
      )}
      
      {!formData.hasDisplayLogic && (
        <p className="text-sm text-gray-500">
          Enable this section to configure what information to show in the scanner app.
        </p>
      )}
    </div>
  );
};

export default DisplayLogicSection;